/**
 * Twilio SMS Service
 * Handles all SMS communications for vendor invitations
 */

import { VendorInvitation } from '../../types/vendorInvitation';

// Twilio Configuration
const TWILIO_ACCOUNT_SID = import.meta.env.VITE_TWILIO_ACCOUNT_SID;
const TWILIO_AUTH_TOKEN = import.meta.env.VITE_TWILIO_AUTH_TOKEN;
const TWILIO_PHONE_NUMBER = import.meta.env.VITE_TWILIO_PHONE_NUMBER;

export interface SMSResult {
  success: boolean;
  messageSid?: string;
  status?: string;
  error?: string;
  errorCode?: string;
}

export interface SMSWebhookPayload {
  MessageSid: string;
  MessageStatus: string;
  From: string;
  To: string;
  Body?: string;
  ErrorCode?: string;
  ErrorMessage?: string;
}

class TwilioService {
  private readonly accountSid: string;
  private readonly authToken: string;
  private readonly phoneNumber: string;

  constructor() {
    this.accountSid = TWILIO_ACCOUNT_SID || '';
    this.authToken = TWILIO_AUTH_TOKEN || '';
    this.phoneNumber = TWILIO_PHONE_NUMBER || '';

    if (this.accountSid && this.authToken && this.phoneNumber) {
    } else {
    }
  }

  /**
   * Check if Twilio is properly configured
   */
  isConfigured(): boolean {
    return !!(this.accountSid && this.authToken && this.phoneNumber);
  }

  /**
   * Send initial vendor invitation SMS
   */
  async sendInvitation(invitation: VendorInvitation): Promise<SMSResult> {
    const message = this.buildInvitationMessage(invitation);
    return await this.sendSMS(invitation.phoneNumber, message);
  }

  /**
   * Send magic link SMS after vendor accepts
   */
  async sendMagicLink(invitation: VendorInvitation): Promise<SMSResult> {
    const message = this.buildMagicLinkMessage(invitation);
    return await this.sendSMS(invitation.phoneNumber, message);
  }

  /**
   * Send reminder SMS if invitation not completed
   */
  async sendReminder(invitation: VendorInvitation): Promise<SMSResult> {
    const message = this.buildReminderMessage(invitation);
    return await this.sendSMS(invitation.phoneNumber, message);
  }

  /**
   * Send confirmation SMS after profile completed
   */
  async sendConfirmation(invitation: VendorInvitation): Promise<SMSResult> {
    const message = this.buildConfirmationMessage(invitation);
    return await this.sendSMS(invitation.phoneNumber, message);
  }

  /**
   * Core SMS sending function using Twilio REST API
   */
  private async sendSMS(to: string, body: string): Promise<SMSResult> {
    if (!this.isConfigured()) {
      console.error('❌ Twilio not configured');
      return {
        success: false,
        error: 'SMS service not configured - missing credentials',
      };
    }

    try {

      // Format phone number to E.164
      const formattedTo = this.formatPhoneNumber(to);

      // Twilio API endpoint
      const url = `https://api.twilio.com/2010-04-01/Accounts/${this.accountSid}/Messages.json`;

      // Create Basic Auth header
      const auth = btoa(`${this.accountSid}:${this.authToken}`);

      // Make the request
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          From: this.phoneNumber,
          To: formattedTo,
          Body: body,
        }).toString(),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Twilio API error:', data);
        return {
          success: false,
          error: data.message || 'Failed to send SMS',
          errorCode: data.code?.toString(),
        };
      }

      return {
        success: true,
        messageSid: data.sid,
        status: data.status,
      };
    } catch (error: any) {
      console.error('❌ SMS send error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send SMS',
      };
    }
  }

  /**
   * Build initial invitation message
   */
  private buildInvitationMessage(invitation: VendorInvitation): string {
    const contactName = invitation.contactName || 'there';
    const services = invitation.metadata?.services || 'services';
    return `Hi ${contactName}, ${invitation.companyName} has invited you to become a vendor for ${services}. Reply YES to accept.`;
  }

  /**
   * Build magic link message
   */
  private buildMagicLinkMessage(invitation: VendorInvitation): string {
    return `Great! Complete your vendor profile here: ${invitation.magicLinkUrl}

This link expires in 48 hours. Need help? Reply HELP`;
  }

  /**
   * Build reminder message
   */
  private buildReminderMessage(invitation: VendorInvitation): string {
    const hoursRemaining = Math.floor(
      (invitation.expiresAt.getTime() - Date.now()) / (1000 * 60 * 60)
    );

    return `Reminder: You have ${hoursRemaining} hours to complete your OilWellX vendor profile: ${invitation.magicLinkUrl}`;
  }

  /**
   * Build confirmation message
   */
  private buildConfirmationMessage(invitation: VendorInvitation): string {
    return `✅ Your vendor profile has been submitted to ${invitation.companyName}. You'll be notified when you're approved and ready to receive service requests.`;
  }

  /**
   * Parse incoming SMS reply
   */
  parseReply(body: string): 'accept' | 'decline' | 'help' | 'unknown' {
    const normalized = body.trim().toLowerCase();

    if (['yes', 'y', 'accept', 'ok', 'sure', 'yeah'].includes(normalized)) {
      return 'accept';
    }

    if (['no', 'n', 'decline', 'nope', 'stop'].includes(normalized)) {
      return 'decline';
    }

    if (['help', 'info', '?'].includes(normalized)) {
      return 'help';
    }

    return 'unknown';
  }

  /**
   * Send help message
   */
  async sendHelpMessage(phoneNumber: string): Promise<SMSResult> {
    const message = `OilWellX Vendor Invitation Help:
• Reply YES to accept the invitation
• Reply NO to decline
• Reply STOP to unsubscribe

Questions? Contact support@oilwellx.com`;

    return await this.sendSMS(phoneNumber, message);
  }

  /**
   * Send error message for unknown replies
   */
  async sendUnknownReplyMessage(phoneNumber: string): Promise<SMSResult> {
    const message = `Sorry, we didn't understand that. Reply YES to accept or NO to decline the vendor invitation. Reply HELP for more options.`;
    return await this.sendSMS(phoneNumber, message);
  }

  /**
   * Format phone number to E.164
   */
  private formatPhoneNumber(phone: string): string {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // If already starts with +, return as is
    if (phone.startsWith('+')) {
      return phone;
    }

    // If 10 digits, assume US number
    if (digits.length === 10) {
      return `+1${digits}`;
    }

    // If 11 digits starting with 1, assume US number
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // Otherwise, add + and return
    return `+${digits}`;
  }

  /**
   * Validate phone number format
   */
  validatePhoneNumber(phone: string): { valid: boolean; formatted?: string; error?: string } {
    try {
      const formatted = this.formatPhoneNumber(phone);
      const digits = formatted.replace(/\D/g, '');

      // Check length (10-15 digits is standard for international)
      if (digits.length < 10 || digits.length > 15) {
        return {
          valid: false,
          error: 'Phone number must be between 10 and 15 digits',
        };
      }

      return {
        valid: true,
        formatted,
      };
    } catch (error) {
      return {
        valid: false,
        error: 'Invalid phone number format',
      };
    }
  }

  /**
   * Get message status from Twilio
   */
  async getMessageStatus(messageSid: string): Promise<SMSResult> {
    if (!this.isConfigured()) {
      return {
        success: false,
        error: 'SMS service not configured',
      };
    }

    try {
      const url = `https://api.twilio.com/2010-04-01/Accounts/${this.accountSid}/Messages/${messageSid}.json`;
      const auth = btoa(`${this.accountSid}:${this.authToken}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || 'Failed to get message status',
        };
      }

      return {
        success: true,
        messageSid: data.sid,
        status: data.status,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get message status',
      };
    }
  }
}

export const twilioService = new TwilioService();
